/**
 * PRODUCT CARD V2 — Stoğa Gelince Haber Ver
 * SAHINKAYA_GUNCELLEMESI
 *
 * Shopify Storefront API subscribeToBackInStockNotification mutation kullanır.
 * Token: window.wishlistConfig.storefrontToken (layout/theme.liquid'de tanımlanır)
 *
 * Gerekli Storefront API scope:
 *   unauthenticated_write_customers
 * (Mevcut wishlist token'ına bu scope eklenmelidir)
 *
 * Scope yoksa veya API başarısız olursa → ürün sayfasına yönlendirir.
 */

'use strict';

class PcV2NotifyWrap {
  constructor(el) {
    this.el         = el;
    this.variantGid = el.dataset.variantGid;
    this.productUrl = el.dataset.productUrl;

    this.trigger = el.querySelector('[data-notify-trigger]');
    this.formRow = el.querySelector('[data-notify-form]');
    this.input   = el.querySelector('[data-notify-input]');
    this.submit  = el.querySelector('[data-notify-submit]');
    this.success = el.querySelector('[data-notify-success]');

    this._bind();
  }

  _bind() {
    if (this.trigger) {
      this.trigger.addEventListener('click', () => this._showForm());
    }
    if (this.submit) {
      this.submit.addEventListener('click', () => this._handleSubmit());
    }
    if (this.input) {
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this._handleSubmit();
      });
      // Hata sınıfını sıfırla
      this.input.addEventListener('input', () => {
        this.input.classList.remove('pcv2__notify-email--error');
      });
    }
  }

  _showForm() {
    this.trigger.hidden = true;
    this.formRow.hidden = false;
    this.input && this.input.focus();
  }

  async _handleSubmit() {
    const email = (this.input ? this.input.value : '').trim();

    // Basit e-posta doğrulama
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (this.input) {
        this.input.classList.add('pcv2__notify-email--error');
        this.input.focus();
      }
      return;
    }

    this.input && this.input.classList.remove('pcv2__notify-email--error');
    if (this.submit) this.submit.disabled = true;

    const subscribed = await this._subscribeStorefrontApi(email);

    if (subscribed) {
      this._showSuccess();
    } else {
      // Fallback: Shopify ürün sayfasına yönlendir
      window.location.href = this.productUrl;
    }
  }

  async _subscribeStorefrontApi(email) {
    const cfg    = window.wishlistConfig || {};
    const token  = cfg.storefrontToken;
    const domain = cfg.shopDomain || window.location.hostname;

    if (!token) return false;

    const query = `
      mutation subscribeBackInStock($input: SubscribeToBackInStockNotificationInput!) {
        subscribeToBackInStockNotification(input: $input) {
          userErrors { field message }
        }
      }
    `;

    try {
      const res = await fetch(`https://${domain}/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query,
          variables: {
            input: { email, variantId: this.variantGid },
          },
        }),
      });

      if (!res.ok) return false;

      const json = await res.json();
      const errors = json?.data?.subscribeToBackInStockNotification?.userErrors;

      // userErrors boş diziyse başarılı
      return Array.isArray(errors) && errors.length === 0;
    } catch (_err) {
      return false;
    }
  }

  _showSuccess() {
    this.formRow.hidden = true;
    this.success.hidden = false;
  }
}

// ------------------------------------------------------------------
// Ürün Sayfası (PDP) Notify — farklı yapı: variant GID formdan okunur
// ------------------------------------------------------------------

class PcV2NotifyPdp {
  constructor(el) {
    this.el         = el;
    this.formId     = el.dataset.formId;
    this.productUrl = el.dataset.productUrl;

    this.trigger = el.querySelector('[data-notify-trigger]');
    this.formRow = el.querySelector('[data-notify-form]');
    this.input   = el.querySelector('[data-notify-input]');
    this.submit  = el.querySelector('[data-notify-submit]');
    this.success = el.querySelector('[data-notify-success]');

    this._bind();
  }

  _bind() {
    if (this.trigger) this.trigger.addEventListener('click', () => this._showForm());
    if (this.submit)  this.submit.addEventListener('click',  () => this._handleSubmit());
    if (this.input) {
      this.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') this._handleSubmit(); });
      this.input.addEventListener('input',   ()  => { this.input.classList.remove('pg-notify-email--error'); });
    }
  }

  _showForm() {
    this.trigger.hidden = true;
    this.formRow.hidden = false;
    this.input && this.input.focus();
  }

  /** Formdaki input[name="id"]'den anlık variant ID'yi okur */
  _getVariantGid() {
    const form = this.formId
      ? document.getElementById(this.formId)
      : this.el.closest('form');
    if (!form) return null;
    const input = form.querySelector('input[name="id"]');
    if (!input || !input.value) return null;
    return 'gid://shopify/ProductVariant/' + input.value;
  }

  async _handleSubmit() {
    const email = (this.input ? this.input.value : '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (this.input) {
        this.input.classList.add('pg-notify-email--error');
        this.input.focus();
      }
      return;
    }
    this.input.classList.remove('pg-notify-email--error');
    if (this.submit) this.submit.disabled = true;

    const variantGid = this._getVariantGid();
    const sent = variantGid
      ? await this._subscribeStorefrontApi(email, variantGid)
      : false;

    if (sent) {
      this._showSuccess();
    } else {
      window.location.href = this.productUrl;
    }
  }

  async _subscribeStorefrontApi(email, variantGid) {
    const cfg    = window.wishlistConfig || {};
    const token  = cfg.storefrontToken;
    const domain = cfg.shopDomain || window.location.hostname;
    if (!token) return false;
    try {
      const res = await fetch(`https://${domain}/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: `mutation subscribeBackInStock($input: SubscribeToBackInStockNotificationInput!) {
            subscribeToBackInStockNotification(input: $input) {
              userErrors { field message }
            }
          }`,
          variables: { input: { email, variantId: variantGid } },
        }),
      });
      if (!res.ok) return false;
      const json = await res.json();
      const errors = json?.data?.subscribeToBackInStockNotification?.userErrors;
      return Array.isArray(errors) && errors.length === 0;
    } catch (_) {
      return false;
    }
  }

  _showSuccess() {
    this.formRow.hidden = true;
    this.success.hidden = false;
  }
}

// ------------------------------------------------------------------
// Init: Sayfadaki ve dinamik yüklenen tüm notify wrap'larını başlat
// ------------------------------------------------------------------

function initPcV2Notify(root) {
  (root || document).querySelectorAll('[data-pcv2-notify]').forEach((el) => {
    if (!el._pcv2Notify) {
      el._pcv2Notify = new PcV2NotifyWrap(el);
    }
  });
  // Ürün sayfası notify
  (root || document).querySelectorAll('[data-pcv2-notify-pdp]').forEach((el) => {
    if (!el._pcv2NotifyPdp) {
      el._pcv2NotifyPdp = new PcV2NotifyPdp(el);
    }
  });
}

// İlk yükleme
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPcV2Notify());
} else {
  initPcV2Notify();
}

// Shopify Section Rendering API (dinamik section yeniden yükleme)
document.addEventListener('shopify:section:load', (e) => initPcV2Notify(e.target));

// ------------------------------------------------------------------
// SAHINKAYA — PDP cart-discount etiketi variant:change ile güncelle
// variant:change; theme.js:3567'de bubbles:true ile dispatch edilir.
// detail.variant.price cent cinsindendir (integer).
// ------------------------------------------------------------------

document.addEventListener('variant:change', (e) => {
  // allow-partial-rerender: galeri değiştirilmez → wishlist state korunur ama
  // .quick-buy-modal__mobile-info fiyatı güncellenmez → JS ile güncelle.
  const variant = e.detail && e.detail.variant;
  const modal   = e.target  && e.target.closest('quick-buy-modal');
  if (!modal || !variant) return;

  const mobileInfo = modal.querySelector('.quick-buy-modal__mobile-info');
  if (!mobileInfo) return;

  // sale-price güncelle
  const salePriceEl = mobileInfo.querySelector('sale-price');
  if (salePriceEl) {
    const srSpan = salePriceEl.querySelector('.sr-only');
    salePriceEl.textContent = pgFormatMoney(variant.price);
    if (srSpan) salePriceEl.prepend(srSpan);
    // on-sale / subdued class
    salePriceEl.classList.remove('text-on-sale', 'text-subdued');
    salePriceEl.classList.add(
      variant.compare_at_price > variant.price ? 'text-on-sale' : 'text-subdued'
    );
  }

  // compare-at-price güncelle
  const compareEl = mobileInfo.querySelector('compare-at-price');
  if (compareEl) {
    if (variant.compare_at_price > variant.price) {
      const srSpan = compareEl.querySelector('.sr-only');
      compareEl.textContent = pgFormatMoney(variant.compare_at_price);
      if (srSpan) compareEl.prepend(srSpan);
      compareEl.hidden = false;
    } else {
      compareEl.hidden = true;
    }
  }
});

document.addEventListener('variant:change', () => {
  // Wishlist buton durumunu geri yükle (her variant değişiminde).
  window.Wishlist?.updateAllButtons();
});

document.addEventListener('variant:change', (e) => {
  const variant = e.detail && e.detail.variant;
  if (!variant || !variant.price) return;

  // Formun bulunduğu product-info container'ını bul
  const form = (e.target && e.target.closest('product-form')) || e.target;
  const productInfo = (form && form.closest('product-info')) || document.querySelector('product-info');
  if (!productInfo) return;

  const tag = productInfo.querySelector('[data-cart-discount-pct]');
  if (!tag) return;

  const pct = parseInt(tag.dataset.cartDiscountPct, 10);
  if (!pct || pct <= 0) return;

  const priceEl = tag.querySelector('.pdp-discount-tag__price');
  if (!priceEl) return;

  // İndirimli fiyat hesapla (Shopify fiyatlar cent cinsinden)
  const discountedCents = Math.round(variant.price * (100 - pct) / 100);
  priceEl.textContent = pgFormatMoney(discountedCents);
});

/**
 * Shopify money formatı — window.Shopify.money_format kullanır.
 * Önek "pg" ile global çakışma önlenir.
 * Fallback: cent / 100 + " TL"
 */
function pgFormatMoney(cents) {
  const moneyFormat = (window.Shopify && window.Shopify.money_format) || '{{amount}} TL';
  const amount = (cents / 100).toFixed(2);
  return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
}
